import React, { useState, useEffect, useRef, useMemo } from "react";
import useGameBase from "../../hooks/useGameBase";
import SocketContext from "../../context/SocketContext";
import SpectatorPerspectiveSelector from "./SpectatorPerspectiveSelector";

export default function UnscrambleGame({ roomId, gameState, gamePlayers = [], user = {} }) {
  // --- Spectator Perspective logic (modular, opt-in, Canasta-style) ---
  const socket = React.useContext(SocketContext);
  const socketId = socket?.id || user?.socketId;
  const myId = user?.userId || socketId;
  // Determine if current user is a spectator
  const isSpectator = Array.isArray(gamePlayers) && !gamePlayers.some(
    (p) => (p.socketId === socketId || p.userId === user?.userId)
  );
  const perspectiveOptions = Array.isArray(gamePlayers) ? gamePlayers : [];
  const [spectatorViewPlayerId, setSpectatorViewPlayerId] = useState(null);
  // Pick first player if none selected
  useEffect(() => {
    if (!isSpectator) {
      setSpectatorViewPlayerId(null);
      return;
    }
    if (!Array.isArray(perspectiveOptions) || perspectiveOptions.length === 0) {
      setSpectatorViewPlayerId(null);
      return;
    }
    const exists = perspectiveOptions.some((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    });
    if (!exists) {
      const first = perspectiveOptions[0];
      setSpectatorViewPlayerId(first?.userId || first?.socketId || first?.id || first?.username || null);
    }
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  // The player whose perspective we're viewing
  const perspectivePlayer = useMemo(() => {
    if (!isSpectator || !spectatorViewPlayerId) return null;
    return perspectiveOptions.find((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    }) || null;
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  // The playerId/socketId to use for progress lookup
  const perspectiveSocketId = isSpectator && perspectivePlayer
    ? perspectivePlayer.socketId || perspectivePlayer.userId || perspectivePlayer.id || perspectivePlayer.username
    : socketId;
  const { submitAction } = useGameBase({
    gameState,
    gamePlayers,
    user,
    socket,
    roomId,
    perspectivePlayer: isSpectator ? perspectivePlayer : undefined,
  });

  // Determine if the current user is a player (not a spectator)
  const isPlayer = !isSpectator;
  const wordPairs = Array.isArray(gameState?.wordPairs) ? gameState.wordPairs : [];
  const playerProgress = gameState?.playerProgress || {};
  const scores = gameState?.scores || {};
  const finished = !!gameState?.finished;
  const winners = Array.isArray(gameState?.winners) ? gameState?.winners : [];
  const result = gameState?.result || {};
  const category = gameState?.category || "Unknown";
  const timeRemaining = Number(gameState?.timeRemaining) || 0;

  const myProgress = playerProgress[perspectiveSocketId] || { unscrambled: [], timestamp: null };
  const unscrambledIndices = Array.isArray(myProgress.unscrambled) ? myProgress.unscrambled : [];
  const winnerBreakdown = Array.isArray(result?.breakdown)
    ? result.breakdown.find((entry) => winners.includes(entry.playerId) || winners.includes(entry.socketId))
    : null;

  // State for UI
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [answer, setAnswer] = useState("");
  const [feedback, setFeedback] = useState(null);
  const inputRef = useRef(null);

  // Focus input when word is selected
  useEffect(() => {
    if (selectedIndex !== null && inputRef.current) {
      inputRef.current.focus();
    }
  }, [selectedIndex]);

  // Clear feedback after 2 seconds
  useEffect(() => {
    if (feedback) {
      const timer = setTimeout(() => setFeedback(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  const handleSelectWord = (index) => {
    if (!isPlayer) return;
    if (unscrambledIndices.includes(index) || finished) return;
    setSelectedIndex(index);
    setAnswer("");
    setFeedback(null);
  };

  const handleSubmit = async () => {
    if (!isPlayer) return;
    if (selectedIndex === null) return;
    const trimmedAnswer = answer.trim();
    if (!trimmedAnswer) return;

    try {
      const ack = await submitAction({
        type: "submit",
        index: selectedIndex,
        answer: trimmedAnswer,
      });

      if (ack && ack.correct) {
        setFeedback("correct");
        setAnswer("");
        // Find the next unsolved word index to the right of the one just solved
        const allIndices = wordPairs.map((_, idx) => idx);
        const solved = new Set([...unscrambledIndices, selectedIndex]);
        const unsolvedIndices = allIndices.filter(idx => !solved.has(idx));
        if (unsolvedIndices.length > 0) {
          // Find the next unsolved index to the right
          const rightOf = unsolvedIndices.find(idx => idx > selectedIndex);
          setSelectedIndex(rightOf !== undefined ? rightOf : unsolvedIndices[0]);
        } else {
          setSelectedIndex(null);
        }
      } else {
        setFeedback("incorrect");
      }
    } catch (e) {
      setFeedback("error");
    }
  };

  // Keyboard navigation for Tab/Shift+Tab cycling through unsolved words
  const handleKeyDown = (e) => {
    if (!isPlayer) return;
    if (e.key === "Enter") {
      handleSubmit();
    } else if (e.key === "Escape") {
      setSelectedIndex(null);
      setAnswer("");
      setFeedback(null);
    } else if (e.key === "Tab") {
      e.preventDefault();
      // Get all unsolved indices
      const allIndices = wordPairs.map((_, idx) => idx);
      const solved = new Set(unscrambledIndices);
      const unsolvedIndices = allIndices.filter(idx => !solved.has(idx));
      if (unsolvedIndices.length === 0) return;

      // If nothing selected, pick first unsolved
      if (selectedIndex === null || !unsolvedIndices.includes(selectedIndex)) {
        setSelectedIndex(unsolvedIndices[0]);
        setAnswer("");
        setFeedback(null);
        return;
      }

      const currentIdx = unsolvedIndices.indexOf(selectedIndex);
      let nextIdx;
      if (e.shiftKey) {
        // Shift+Tab: go left
        nextIdx = currentIdx > 0 ? currentIdx - 1 : unsolvedIndices.length - 1;
      } else {
        // Tab: go right
        nextIdx = currentIdx < unsolvedIndices.length - 1 ? currentIdx + 1 : 0;
      }
      setSelectedIndex(unsolvedIndices[nextIdx]);
      setAnswer("");
      setFeedback(null);
    }
  };

  const handleClear = () => {
    setSelectedIndex(null);
    setAnswer("");
    setFeedback(null);
  };

  // Separate words into unscrambled and scrambled
  const unscrambledWords = wordPairs.filter((_, idx) => {
    return unscrambledIndices.includes(idx);
  });
  const scrambledWords = wordPairs.filter((_, idx) => {
    return !unscrambledIndices.includes(idx);
  });

  const progress = `${unscrambledIndices.length}/${wordPairs.length}`;

  const formatTime = (secs) => {
    const s = Math.max(0, Number(secs) || 0);
    const mm = Math.floor(s / 60)
      .toString()
      .padStart(2, "0");
    const ss = Math.floor(s % 60)
      .toString()
      .padStart(2, "0");
    return `${mm}:${ss}`;
  };

  return (
    <div className="p-4 w-full max-w-2xl mx-auto dark:bg-gray-900 bg-white text-gray-900 dark:text-gray-100">
      {/* Spectator Perspective Selector (Canasta-style) */}
      {isSpectator && perspectiveOptions.length > 0 && (
        <SpectatorPerspectiveSelector
          players={perspectiveOptions}
          value={spectatorViewPlayerId || ""}
          onChange={setSpectatorViewPlayerId}
          className="mb-4"
        />
      )}
      {/* Header */}
      <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900 dark:to-indigo-900 rounded-lg border-2 border-blue-300 dark:border-blue-700">
        <div className="text-center">
          <div className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-2">
            Category
          </div>
          <h1 className="text-4xl font-bold text-blue-700 dark:text-blue-300 mb-3 capitalize">
            {category}
          </h1>
          <div className="text-lg font-semibold text-gray-700 dark:text-gray-300">
            {progress} Unscrambled
          </div>
          <div className="text-sm font-medium text-gray-600 dark:text-gray-300 mt-2">
            Time Remaining: <span className="font-mono">{formatTime(timeRemaining)}</span>
          </div>

        </div>
      </div>

      {/* Main Game Area */}
      {!finished && (
        <div>
          {/* Scrambled Words */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
              Words to Unscramble
              {typeof window !== "undefined" &&
                (() => {
                  // Basic detection for keyboard presence (not mobile/tablet)
                  const ua = navigator.userAgent;
                  const isTouch =
                    /Mobi|Android|iPhone|iPad|iPod|Mobile|Tablet|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);
                  if (!isTouch) {
                    return (
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(use Shift+Tab / Tab to navigate)</span>
                    );
                  }
                  return null;
                })()}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
              {scrambledWords.map((wordPair, localIdx) => {
                const actualIdx = wordPairs.findIndex(
                  (wp) => wp.original === wordPair.original && wp.scrambled === wordPair.scrambled
                );
                const isSelected = selectedIndex === actualIdx;
                const baseClass =
                  "p-3 border rounded-lg text-center font-mono text-sm cursor-pointer transition-all";
                const selectedClass = isSelected
                  ? "bg-blue-500 text-white border-blue-600 dark:border-blue-400"
                  : "bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 border-gray-300 dark:border-gray-600";

                return (
                  <div
                    key={`scrambled-${actualIdx}`}
                    className={`${baseClass} ${selectedClass}`}
                    onClick={() => handleSelectWord(actualIdx)}
                  >
                    {wordPair.scrambled.toUpperCase()}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Input Area */}
          {selectedIndex !== null && isPlayer && (
            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
              <div className="mb-3">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  <span className="font-mono text-blue-700 dark:text-blue-300">{wordPairs[selectedIndex]?.scrambled?.toUpperCase()}</span> unscrambled is:
                </label>
                <input
                  ref={inputRef}
                  type="text"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your answer..."
                  className="w-full mt-2 p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={!isPlayer}
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSubmit}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-semibold"
                  disabled={!isPlayer}
                >
                  Submit
                </button>
                <button
                  onClick={handleClear}
                  className="flex-1 px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500 font-semibold"
                >
                  Cancel
                </button>
              </div>
              {feedback && (
                <div
                  className={`mt-2 text-center font-semibold ${
                    feedback === "correct"
                      ? "text-green-600"
                      : feedback === "incorrect"
                      ? "text-red-600"
                      : "text-yellow-600"
                  }`}
                >
                  {feedback === "correct" && "✓ Correct!"}
                  {feedback === "incorrect" && "✗ Incorrect"}
                  {feedback === "error" && "Error submitting"}
                </div>
              )}
            </div>
          )}

          {/* Unscrambled Words */}
          {unscrambledWords.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold mb-2 text-green-700 dark:text-green-300">
                Completed
              </h3>
              <div className="flex flex-wrap gap-2">
                {unscrambledWords.map((wordPair, idx) => (
                  <div
                    key={`unscrambled-${idx}`}
                    className="px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100 rounded-full text-sm font-semibold flex items-center gap-1"
                  >
                    ✓ {wordPair.original.toUpperCase()}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Game Finished */}
      {finished && (
        <div className="mt-6 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg text-center">
          <h2 className="text-2xl font-bold mb-4">
            {winners.includes(socketId) ? "🎉 You Won!" : `Winner: ${(result.winnerNames && result.winnerNames[0]) || "Unknown"}`}
          </h2>
          {winnerBreakdown?.completionTimeDisplay && (
            <p className="text-sm text-emerald-700 dark:text-emerald-300 mb-3 font-semibold">
              Finished in {winnerBreakdown.completionTimeDisplay}
            </p>
          )}
          {result.message && (
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 font-semibold">
              {result.message}
            </p>
          )}
          <div className="mb-6 text-left">
            <h3 className="font-semibold mb-3 text-center">Your Words</h3>
            <div className="space-y-1">
              {wordPairs.map((wordPair, idx) => {
                const gotWord = unscrambledIndices.includes(idx);
                return (
                  <div
                    key={`final-${idx}`}
                    className={`text-sm ${gotWord ? "text-green-700 dark:text-green-300" : "text-red-700 dark:text-red-300"}`}
                  >
                    {gotWord ? "✓" : "✗"} {wordPair.original.toUpperCase()}
                  </div>
                );
              })}
            </div>
          </div>
          <h3 className="font-semibold mb-3">Final Scores:</h3>
          <div className="space-y-1">
            {gamePlayers.map((player) => {
              const playerScore = scores[player.socketId] || scores[player.userId] || 0;
              const isWinner = winners.includes(player.socketId) || winners.includes(player.userId);
              return (
                <div
                  key={player.socketId || player.userId}
                  className={`text-sm flex justify-between ${
                    isWinner ? "font-bold text-green-600 dark:text-green-400" : ""
                  }`}
                >
                  <span>{player.username || "Player"} {isWinner && "👑"}</span>
                  <span className="font-semibold">
                    {playerScore}
                    {playerProgress[player.socketId || player.userId]?.completionTimeDisplay
                      ? ` • ${playerProgress[player.socketId || player.userId].completionTimeDisplay}`
                      : ''}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Scores Sidebar */}
      <div className="mt-6 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
          PLAYER PROGRESS
        </h3>
        <div className="space-y-1 text-xs">
          {gamePlayers.map((player) => {
            const playerId = player.socketId || player.userId;
            const playerProg = playerProgress[playerId];
            const playerUnscrambled = Array.isArray(playerProg?.unscrambled) ? playerProg.unscrambled : [];
            const playerCount = playerUnscrambled.length;
            return (
              <div
                key={playerId}
                className="flex justify-between items-center"
              >
                <span className={player.socketId === socketId ? "font-semibold" : ""}>
                  {player.username || "Player"}:
                </span>
                <span className="text-gray-600 dark:text-gray-400">
                  {playerCount}/{wordPairs.length}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

UnscrambleGame.Guide = function UnscrambleGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to Play Unscramble</h3>
      <ul className="list-disc ml-6 space-y-1 text-sm">
        <li>You will see a list of scrambled words.</li>
        <li>Click on a scrambled word to select it.</li>
        <li>Type the correct unscrambled word in the input box.</li>
        <li>Click Submit or press Enter to check your answer.</li>
        <li>Correct words move to the "Completed" section.</li>
        <li>First player to unscramble all words wins!</li>
      </ul>
    </div>
  );
};
